import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import axios from 'axios';

import { HTTP_STATUS } from '@/constants/app';
import { API_CONFIG, ENDPOINTS } from '@/constants/endpoints';
import {
  clearAuthStorage,
  getAccessToken,
  getRefreshToken,
} from '@/lib/auth/authUtils';
import i18n from '@/lib/i18n';
import { useAuthStore } from '@/store/authStore';
import type { ApiError, ResponseMessage } from '@/types/api';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}[] = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.request.use(
  config => {
    // Cast config to include custom properties
    const requestConfig = config as AxiosRequestConfig & { _retry?: boolean };

    // If this is a retry (triggered by response interceptor after refresh),
    // we should rely on the Authorization header already set by the refresh logic
    if (requestConfig._retry) {
      return config;
    }

    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add language header
    config.headers['Accept-Language'] = i18n.language;

    return config;
  },
  error => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response: AxiosResponse<ResponseMessage>) => response,
  async (error: AxiosError<ResponseMessage>) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    if (
      error.response?.status === HTTP_STATUS.UNAUTHORIZED &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        processQueue(new Error('No refresh token'), null);
        clearAuthStorage();
        isRefreshing = false;
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post<
          ResponseMessage<{
            accessToken: string;
            refreshToken: string;
          }>
        >(`${API_CONFIG.BASE_URL}${ENDPOINTS.AUTH.REFRESH}`, { refreshToken });

        const payload = response.data.payload;
        if (!payload) {
          throw new Error('Invalid refresh token response');
        }

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
          payload;

        // Sync with Zustand store (which also updates localStorage via persist middleware and our custom setTokens)
        // We don't need updateTokensInStorage anymore as the store handles it
        useAuthStore.getState().setTokens(newAccessToken, newRefreshToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        processQueue(null, newAccessToken);
        isRefreshing = false;

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        isRefreshing = false;
        clearAuthStorage();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    const apiError: ApiError = {
      status: error.response?.status || HTTP_STATUS.INTERNAL_SERVER_ERROR,
      message:
        error.response?.data?.message ||
        error.message ||
        'An unexpected error occurred',
      detail: error.response?.data?.detail ?? undefined,
      path: error.response?.data?.path ?? undefined,
    };

    // Note: Don't show error toast here - let React Query's MutationCache handle it
    // to avoid duplicate toasts
    // Global 401 handling: Redirect to login and clear everything
    // Skip for login requests to allow showing validation errors like "Invalid password"
    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
      useAuthStore.getState().logout();
      clearAuthStorage();
      window.location.href = '/login';
    }

    return Promise.reject(apiError);
  },
);
export async function get<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.get<ResponseMessage<T>>(url, config);
  return response.data.payload as T;
}

export async function post<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.post<ResponseMessage<T>>(url, data, config);
  return response.data.payload as T;
}

export async function put<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.put<ResponseMessage<T>>(url, data, config);
  return response.data.payload as T;
}

export async function del<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.delete<ResponseMessage<T>>(url, config);
  return response.data.payload as T;
}

export async function patch<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.patch<ResponseMessage<T>>(url, data, config);
  return response.data.payload as T;
}

export async function upload<T>(
  url: string,
  file: File,
  config?: AxiosRequestConfig,
): Promise<T> {
  const formData = new FormData();
  formData.append('file', file);

  const uploadConfig: AxiosRequestConfig = {
    ...config,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...config?.headers,
    },
  };

  const response = await apiClient.post<ResponseMessage<T>>(
    url,
    formData,
    uploadConfig,
  );
  return response.data.payload as T;
}

export async function download(
  url: string,
  fileName?: string,
  config?: AxiosRequestConfig,
): Promise<void> {
  const downloadConfig: AxiosRequestConfig = {
    ...config,
    responseType: 'blob',
  };

  const response = await apiClient.get<Blob>(url, downloadConfig);
  const blob = response.data;

  const urlBlob = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  try {
    link.href = urlBlob;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
  } finally {
    if (link.parentNode) {
      document.body.removeChild(link);
    }
    window.URL.revokeObjectURL(urlBlob);
  }
}

export default apiClient;
